import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';
import { Song } from '../components/song';
import model from '../resources/model/model';
import './SongPage.scss';

const CHEAT_BUFFER_TIMEOUT_MS = 2200;

//a swipe only turns the page when it is a quick, clearly sideways, one-finger
//flick across a good part of the screen - scrolling, pinch-zooming or moving
//around a zoomed-in page must never change the song
const SWIPE_MAX_DURATION_MS = 500;
const SWIPE_SCREEN_FRACTION = 1 / 3;  //of the screen width...
const SWIPE_MAX_DISTANCE_PX = 150;    //...but never more than this on wide screens
const SWIPE_SIDEWAYS_RATIO = 2;       //at least twice as far sideways as up or down
const SWIPE_SCROLL_SHARE = 1 / 3;     //a drag that scrolled this share of its length was a scroll

//where every sideways-scrollable box under the finger stands, so a drag that
//scrolled one of them counts as a scroll and not a swipe
function horizontalScrollPositions(target) {
    const positions = [];
    for (let el = target; el instanceof Element; el = el.parentElement)
        if (el.scrollWidth > el.clientWidth) positions.push([el, el.scrollLeft]);
    return positions;
}

//how far a box really scrolled. offsets are clamped to its scroll range, so an
//ios rubber-band bounce at the edge does not count - and a box only a few px
//too wide (a chord sticking out) cannot scroll enough to swallow a swipe
function scrolledBy(el, from) {
    const clamp = x => Math.min(Math.max(x, 0), el.scrollWidth - el.clientWidth);
    return Math.abs(clamp(el.scrollLeft) - clamp(from));
}

function isFormElementFocused() {
    const active = document.activeElement;
    const tag = active && active.tagName ? active.tagName.toUpperCase() : null;
    return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || tag === "BUTTON";
}

function normalizeCheatKey(key = "") {
    return key.length === 1 && /^[a-z]$/i.test(key) ? key.toLowerCase() : "";
}

export default function SongPage() {
    const song = model.currentSong || {};
    const songIDs = model.songIDs;
    //a filter can leave nothing to move to - keep the arrows on the current song
    //instead of sending them to "/undefined"
    const next = songIDs[(songIDs.indexOf(song.id) + 1) % (songIDs.length)] || song.id || ""
    const prev = songIDs[(songIDs.indexOf(song.id) + songIDs.length - 1) % (songIDs.length)] || song.id || ""
    const isPonifiedSong = !!model.settings.ponify && Array.isArray(song.genre) && song.genre.includes("ponici");

    const navigate = useNavigate()
    const [usedCheatcode, setUsedCheatcode] = useState(null);
    const [cheatCommand, setCheatCommand] = useState(null);
    const [villainControls, setVillainControls] = useState({
        up: false,
        down: false,
        left: false,
        right: false,
        teleportTick: 0,
        blastTick: 0,
    });
    const cheatBufferRef = useRef("");
    const cheatBufferTimestampRef = useRef(0);

    useEffect(() => {
        cheatBufferRef.current = "";
        cheatBufferTimestampRef.current = 0;
        setUsedCheatcode(null);
        setCheatCommand(null);
        setVillainControls({ up: false, down: false, left: false, right: false, teleportTick: 0, blastTick: 0 });
    }, [song.id]);

    //a session update may be waiting for this song to be on screen to fit it
    useEffect(() => { model.onSongShown(song.id) }, [song.id]);

    //what the current touch has done so far. it is watched on the whole window
    //because a second finger can land anywhere, even off the song
    const gestureRef = useRef({ multiTouch: false, start: null, scrollPositions: [] });
    useEffect(() => {
        function onTouchStart(event) {
            if (event.touches.length > 1) gestureRef.current.multiTouch = true;
            else gestureRef.current = {
                multiTouch: false,
                start: [event.touches[0].clientX, event.touches[0].clientY],
                scrollPositions: horizontalScrollPositions(event.target),
            };
        }
        const options = { capture: true, passive: true };
        window.addEventListener("touchstart", onTouchStart, options);
        return () => window.removeEventListener("touchstart", onTouchStart, options);
    }, []);

    //the setting is read here and not by leaving the handlers off the page -
    //react-swipeable never detaches its listeners once attached
    function isDeliberateSwipe({ absX, absY, event }) {
        const gesture = gestureRef.current;
        if (!model.settings.swipeGesturesPrevNext || gesture.multiTouch) return false;
        if ((window.visualViewport?.scale || 1) > 1.01) return false;
        //react-swipeable measures to the last move - a quick flick's final
        //stretch only shows up where the finger lifts
        const lift = event?.changedTouches?.[0];
        if (lift && gesture.start) {
            absX = Math.abs(lift.clientX - gesture.start[0]);
            absY = Math.abs(lift.clientY - gesture.start[1]);
        }
        if (absX < Math.min(window.innerWidth * SWIPE_SCREEN_FRACTION, SWIPE_MAX_DISTANCE_PX)) return false;
        if (absX < absY * SWIPE_SIDEWAYS_RATIO) return false;
        return !gesture.scrollPositions.some(([el, from]) => scrolledBy(el, from) >= absX * SWIPE_SCROLL_SHARE);
    }

    const handlers = useSwipeable({
        swipeDuration: SWIPE_MAX_DURATION_MS,
        onSwipedLeft: (swipe) => { if (isDeliberateSwipe(swipe)) navigate("/" + next) },
        onSwipedRight: (swipe) => { if (isDeliberateSwipe(swipe)) navigate("/" + prev) },
    })

    useEffect(() => {
        function onKeyDown(event) {
            if (isFormElementFocused()) return;

            //ctrl+c transposes the chords up by the capo so the song plays open,
            //the same as clicking the capo - but let a real copy through when the
            //user has actually selected something
            if ((event.ctrlKey || event.metaKey) && !event.altKey && event.key.toLowerCase() === "c") {
                const current = model.currentSong;
                if (current?.capo && !window.getSelection()?.toString()) {
                    event.preventDefault();
                    model.toggleCapo(current);
                }
                return;
            }

            //ctrl+e fits the song to the screen or undoes it, like the auto-fit button
            if ((event.ctrlKey || event.metaKey) && !event.altKey && event.key.toLowerCase() === "e") {
                event.preventDefault();
                //holding the keys down must not flip the fit on and off
                if (!event.repeat) model.toggleAutoFit();
                return;
            }

            const key = event.key.toLowerCase();
            const activeMovementVillain = usedCheatcode === "discord" || usedCheatcode === "nightmare";

            if (isPonifiedSong && activeMovementVillain) {
                if (key === "w") setVillainControls((controls) => ({ ...controls, up: true }));
                if (key === "a") setVillainControls((controls) => ({ ...controls, left: true }));
                if (key === "s") setVillainControls((controls) => ({ ...controls, down: true }));
                if (key === "d") setVillainControls((controls) => ({ ...controls, right: true }));
                if (usedCheatcode === "discord" && key === "t" && !event.repeat) {
                    setVillainControls((controls) => ({ ...controls, teleportTick: controls.teleportTick + 1 }));
                }
            }

            if (isPonifiedSong && (usedCheatcode === "nightmare" || usedCheatcode === "tirek") && key === "b" && !event.repeat) {
                setVillainControls((controls) => ({ ...controls, blastTick: controls.blastTick + 1 }));
            }

            if (!isPonifiedSong || usedCheatcode || event.repeat) return;

            const letter = normalizeCheatKey(event.key);
            if (!letter) return;

            const now = performance.now();
            const buffer = now - cheatBufferTimestampRef.current > CHEAT_BUFFER_TIMEOUT_MS
                ? letter
                : (cheatBufferRef.current + letter).slice(-12);

            cheatBufferRef.current = buffer;
            cheatBufferTimestampRef.current = now;

            const matched = buffer.endsWith("nightmare") && !model.settings.darkMode
                ? "nightmare"
                : buffer.endsWith("discord")
                    ? "discord"
                    : buffer.endsWith("lordtirek")
                        ? "tirek"
                    : buffer.endsWith("out")
                        ? "out"
                        : null;
            if (!matched) return;

            setUsedCheatcode(matched);
            setCheatCommand({ type: matched, token: now, songId: song.id });
            cheatBufferRef.current = "";
            cheatBufferTimestampRef.current = 0;
        }

        function onKeyUp(event) {
            if (isFormElementFocused()) return;

            if (event.key === "ArrowRight") navigate("/" + next);
            if (event.key === "ArrowLeft") navigate("/" + prev);

            if (!isPonifiedSong || (usedCheatcode !== "discord" && usedCheatcode !== "nightmare")) return;

            const key = event.key.toLowerCase();
            if (key === "w") setVillainControls((controls) => ({ ...controls, up: false }));
            if (key === "a") setVillainControls((controls) => ({ ...controls, left: false }));
            if (key === "s") setVillainControls((controls) => ({ ...controls, down: false }));
            if (key === "d") setVillainControls((controls) => ({ ...controls, right: false }));
        }

        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("keyup", onKeyUp);

        return () => {
            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("keyup", onKeyUp);
        };
    }, [isPonifiedSong, navigate, next, prev, song.id, usedCheatcode]);

    return <>
        <Link className="notA" to={"/" + prev}><div className="prev"><i className="fas fa-chevron-left"></i></div></Link>
        <Link className="notA" to={"/" + next}><div className="next"><i className="fas fa-chevron-right"></i></div></Link>
        <div className="page" id="song-page" {...handlers}>
            <Song
                song={song}
                columns={model.settings.columns}
                fontSize={model.fontSize}
                trans={model.settings.transpose}
                transposeCapo={model.settings.transposeCapo}
                instrument={model.settings.chords}
                ponyCommand={cheatCommand}
                villainControls={villainControls}
            />
        </div>
    </>
}